import * as React from 'react';

import { ensureValue } from '@rematter/ui-web';
import {
  Checkbox,
  DatePicker,
  NumberField,
  Select,
  TextField,
  TimeField,
} from '@rematter/ui-web';
import startCase from 'lodash/startCase';

import { ObjectRefSelect } from './ObjectRefSelect';
import { CustomAttributeRecord } from './types';

import { useCoreUsersQuery } from '~/shared/graphql/queries/useCoreUsersQuery';
import { useCRMAccountAddressesQuery } from '~/shared/graphql/queries/useCRMAccountAddressesQuery';
import { useCRMAccountsQuery } from '~/shared/graphql/queries/useCRMAccountsQuery';
import { useCRMContactsQuery } from '~/shared/graphql/queries/useCRMContactsQuery';
import { useFacilityGroupsQuery } from '~/shared/graphql/queries/useFacilityGroupsQuery';
import { useFacilitiesQuery } from '~/shared/graphql/queries/useFacilityQuery';
import { useFinancialAccountsQuery } from '~/shared/graphql/queries/useFinancialAccountsQuery';
import { useInventoryCategoriesQuery } from '~/shared/graphql/queries/useInventoryCategoriesQuery';
import { useInventoryItemGroupsQuery } from '~/shared/graphql/queries/useInventoryItemGroupsQuery';
import { useInventoryItemsQuery } from '~/shared/graphql/queries/useInventoryItemsQuery';
import { useInventoryTagsQuery } from '~/shared/graphql/queries/useInventoryTagsQuery';
import { useTranslations } from '~/shared/i18n/useTranslations';

type CustomAttributeFieldProps = {
  definition: CustomAttributeRecord;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
};

export const CustomAttributeField = ({
  value,
  onChange,
  definition,
  disabled,
}: CustomAttributeFieldProps) => {
  const t = useTranslations();

  const formattedName = startCase(definition.name);

  const commonProps = {
    label: formattedName,
    isRequired: !!definition.constraints?.required,
    isDisabled: disabled,
    value: value as never,
    onChange,
  };

  const selectOptions = React.useMemo(
    () =>
      definition.constraints?.selectOptions?.map((it) => ({
        key: it,
        label: it,
      })) ?? [],
    [definition.constraints?.selectOptions],
  );

  const lengthConstraints = React.useMemo(
    () => ({
      minLength: definition.constraints?.minLength ?? undefined,
      maxLength: definition.constraints?.maxLength ?? undefined,
    }),
    [definition.constraints?.minLength, definition.constraints?.maxLength],
  );

  const minMaxValueConstraints = React.useMemo(
    () => ({
      minValue: definition.constraints?.minValue ?? undefined,
      maxValue: definition.constraints?.maxValue ?? undefined,
    }),
    [definition.constraints?.minValue, definition.constraints?.maxValue],
  );

  // 🔵🔵🔵 Text / MultilineText / UUID
  if (
    definition.dataType === 'Text' ||
    definition.dataType === 'MultilineText' ||
    definition.dataType === 'UUID'
  )
    return (
      <TextField
        rows={definition.dataType === 'MultilineText' ? 2 : undefined}
        {...commonProps}
        {...lengthConstraints}
      />
    );

  // 🔵🔵🔵 Number
  if (definition.dataType === 'Number')
    return <NumberField {...commonProps} {...minMaxValueConstraints} />;

  // 🔵🔵🔵 Money
  if (definition.dataType === 'Money')
    return (
      <NumberField
        {...commonProps}
        {...minMaxValueConstraints}
        formatOptions={{
          style: 'currency',
          currency: 'USD',
        }}
      />
    );

  // 🔵🔵🔵 Boolean
  if (definition.dataType === 'Boolean')
    return (
      <Checkbox {...commonProps} isSelected={Boolean(value)}>
        {formattedName}
      </Checkbox>
    );

  // 🔵🔵🔵 Date
  if (definition.dataType === 'Date') return <DatePicker {...commonProps} />;

  // 🔵🔵🔵 DateTime
  if (definition.dataType === 'DateTime')
    return (
      <div className="grid grid-cols-5 gap-2">
        <DatePicker {...commonProps} className="col-span-3" />
        <TimeField {...commonProps} className="col-span-2" />
      </div>
    );

  // 🔵🔵🔵 Time
  if (definition.dataType === 'Time') return <TimeField {...commonProps} />;

  // 🔵🔵🔵 Select
  if (definition.dataType === 'Select')
    return (
      <Select<string>
        label={commonProps.label}
        value={commonProps.value as string}
        onChange={commonProps.onChange}
        isRequired={commonProps.isRequired}
        options={selectOptions}
      />
    );

  // 🔵🔵🔵 ObjectRef / FacilityGroup
  if (definition.dataType === 'ObjectRef') {
    const objectRefProps = {
      placeholder:
        t.selectFields[ensureValue(definition.constraints?.objectType)]
          .placeholder,
      ...commonProps,
    };

    switch (definition.constraints?.objectType) {
      case 'CoreFacilityGroup':
        return (
          <ObjectRefSelect
            queryHook={useFacilityGroupsQuery}
            {...objectRefProps}
          />
        );

      case 'CRMContact':
        return (
          <ObjectRefSelect
            queryHook={useCRMContactsQuery}
            {...objectRefProps}
          />
        );

      case 'CRMAccount':
        return (
          <ObjectRefSelect
            queryHook={useCRMAccountsQuery}
            {...objectRefProps}
          />
        );

      case 'CRMAccountAddress':
        return (
          <ObjectRefSelect
            queryHook={useCRMAccountAddressesQuery}
            {...objectRefProps}
          />
        );

      case 'CoreFacility':
        return (
          <ObjectRefSelect queryHook={useFacilitiesQuery} {...objectRefProps} />
        );

      case 'CoreUser':
        return (
          <ObjectRefSelect queryHook={useCoreUsersQuery} {...objectRefProps} />
        );

      case 'FilesFile':
        return <div>TBI</div>;

      case 'FinanceFinancialAccount':
        return (
          <ObjectRefSelect
            queryHook={useFinancialAccountsQuery}
            {...objectRefProps}
          />
        );

      case 'InventoryCategory':
        return (
          <ObjectRefSelect
            queryHook={useInventoryCategoriesQuery}
            {...objectRefProps}
          />
        );

      case 'InventoryItem':
        return (
          <ObjectRefSelect
            queryHook={useInventoryItemsQuery}
            {...objectRefProps}
          />
        );

      case 'InventoryItemGroup':
        return (
          <ObjectRefSelect
            queryHook={useInventoryItemGroupsQuery}
            {...objectRefProps}
          />
        );

      case 'InventoryTag':
        return (
          <ObjectRefSelect
            queryHook={useInventoryTagsQuery}
            {...objectRefProps}
          />
        );

      default:
        return null;
    }
  }

  return null;
};
